import React, { useContext } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'
import Fab from '../fab/Fab'
import { FieldRenderProps } from 'react-final-form'
import { FormApi } from 'final-form'
import styles from '../../styles'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { ResponsiveContext } from '../../utils/context/responsiveContext'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => (
		{
			...styles(theme),
			label: {
				...styles(theme).text5,
				color: theme.palette.blue.main,
				marginBottom: props => props.isLargeScreen ? 15 : 45
			},
			fabContainer: {
				'& button': {
					boxShadow: 'none',
					width: '100%',
					minHeight: 51,
					...styles(theme).text4,
					fontSize: 12,
					textTransform: 'initial'
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: 10,
				paddingBottom: 5
			},
			svgStyle: {
				marginBottom: 5,
				marginRight: 10
			}
		}
	)
)

// TODO Mettre un type générique sur les any quand temps dispo
interface AcceptingProps {
	form: FormApi
	label: string,
	elements: string[],
	enumToParse: any,
	multipleValue?: boolean
}

type FormFabFieldProps =
	AcceptingProps &
	FieldRenderProps<string | number | string[]>

const FormFabField: React.FC<FormFabFieldProps> = (
	{
		input,
		meta,
		form,
		label,
		elements,
		enumToParse,
		multipleValue = false
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })

	// Changement de la valeur du formulaire
	const changeFormValue = (element: string) => {
		let finalValues

		// Si la sélection du fab est en multiple et que nos valeurs sont un tableau
		// normalement, c'est toujours un array si multiple = true
		if (multipleValue && Array.isArray(input.value)) {
			// Si l'élément est déjà sélectionné, on l'enlève du tableau des valeurs
			if (checkIfSelected(element)) {
				finalValues = input.value.filter((value) => value !== enumToParse[element])
			}
			// Sinon, on ajoute le nouvel élément à la liste des valeurs
			else {
				finalValues = [
					...input.value,
					enumToParse[element]
				]
			}
		}
		// Sinon on est en sélection unique
		else {
			finalValues = enumToParse[element]
		}

		form.change(input.name, finalValues)
	}

	// Vérification de la présence de l'élément parcouru dans les valeurs du formulaire
	const checkIfSelected = (element: string) => {
		// Si la sélection du fab est en multiple et que nos valeurs sont un tableau
		// normalement, c'est toujours un array si multiple = true
		if (multipleValue && Array.isArray(input.value)) {
			return input.value.includes(enumToParse[element])
		}

		return Number(input.value) === enumToParse[element]
	}

	return (
		<>
			<Grid container item xs={12}>
				<span
					className={classNames(
						classes.label,
						{
							[classes.errorStyle]: meta.touched && meta.error
						}
					)}
				>
					{label}
				</span>
			</Grid>

			<Grid container item xs={12} spacing={2}>
				{
					elements
						.map(
							(element, index) => (
								<Grid item xs={6} md={2} className={classes.fabContainer} key={element}>
									<Fab
										ariaLabel={element}
										onClick={() => changeFormValue(element)}
										isSelected={checkIfSelected(element)}
										label={<FormattedMessage
											id={`fabField.${input.name}.${element}`}
											defaultMessage="Élément du fab"
											description="Label du choix du fab"
										/>}
									/>
								</Grid>
							)
						)
				}
			</Grid>

			{
				meta.touched &&
				((meta.error && <span className={classes.errorStyle}>{meta.error}</span>))
			}
		</>
	)
}

export default FormFabField
